import {NgbDateParserFormatter, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {inject, Injectable} from '@angular/core';
import {DateTime, DateTimeFormatOptions} from 'luxon';
import {TranslateService} from '@brunata-angular/util';
import {
    DANISH_DATE_SEPARATOR,
    expandTwoDigitYear,
    formatDateTime,
    getParseFormat,
} from '@brunata-angular/util';

@Injectable()
export class DateParserFormatter extends NgbDateParserFormatter {
    private dateFormat: DateTimeFormatOptions = {year: 'numeric', month: '2-digit', day: '2-digit'};
    private readonly danishDateSeparator = inject(DANISH_DATE_SEPARATOR);

    constructor(private translateService: TranslateService) {
        super();
    }

    parse(value: string): NgbDateStruct | null {
        if (!value) {
            return null;
        }
        const format = getParseFormat(
            this.dateFormat,
            this.translateService.language,
            this.danishDateSeparator,
        );
        if (format != null) {
            const {isValid, year, month, day} = DateTime.fromFormat(value, format);
            if (isValid) {
                return {year: year, month: month, day: day};
            }
        }
        // Fast entry (AGI-17884): a bare all-digit string with no separators, keyed as ddMMyy or
        // ddMMyyyy, so operators can type a date without the separator key. Both supported locales
        // (da, en-GB) are day-first, so the compact order matches regardless of language.
        return this.parseCompactDate(value);
    }

    private parseCompactDate(value: string): NgbDateStruct | null {
        if (!/^(?:\d{6}|\d{8})$/.test(value)) {
            return null;
        }
        // Expanded up front because luxon's own `ddMMyy` pivots on a fixed global cutoff.
        const compactDate =
            value.length === 6
                ? `${value.slice(0, 4)}${expandTwoDigitYear(Number(value.slice(4)), DateTime.now())}`
                : value;
        const {isValid, year, month, day} = DateTime.fromFormat(compactDate, 'ddMMyyyy');
        return isValid ? {year: year, month: month, day: day} : null;
    }

    format(ngbDateStruct: NgbDateStruct | null): string {
        if (!ngbDateStruct) {
            return '';
        }
        return formatDateTime(
            DateTime.fromObject(ngbDateStruct),
            this.translateService.language,
            this.dateFormat,
            this.danishDateSeparator,
        );
    }
}
